import { gql, useQuery } from '@apollo/client';
import { type CalendarDate } from '@internationalized/date';
import Button from '@orn/shared-web/components/Button/Button';
import CollapseButton from '@orn/shared-web/components/Button/CollapseButton';
import EditButton from '@orn/shared-web/components/Button/EditButton';
import Table, { TableCard } from '@orn/shared-web/components/Table';
import Tooltip, { TooltipTrigger } from '@orn/shared-web/components/Tooltip';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import { PaginationDirection, TransactionSortKeys, type TransactionFilters } from '../../../__generated__/globalTypes';
import { useContainerBreakpoint } from '../../../hooks/useContainerBreakpoint';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { formatCalendarDate, formatDaysUntil, toCurrency } from '../../../utils/formatting';
import {
  calculateYearlySpend,
  formatRecurringType,
  getAverageAmountLabel,
  getFrequencyLabel,
} from '../../../utils/spending/recurring';
import { useDateRangeDisplayText, type CalendarDateRange } from '../../DateRangePicker';
import Entity from '../../entity';
import UpdateRecurringTransactionModal from '../../UpdateRecurringTransactionModal';
import { AccountSecondaryElement } from '../elements';
import { EmptyTableState, ErrorTableState, LoadingTableState, type IEmptyTableStateProps } from '../helpers';
import useHasTransactionsForEmptyState from '../hooks/useHasTransactionsForEmptyState';
import { useToggledStreamsParam } from '../hooks/useTableSearchParams';

import type {
  GetRecurringStreamTransactionsQuery,
  GetRecurringStreamTransactionsQueryVariables,
  RecurringStreamFragment,
} from './__generated__/RecurringStreamTable.gql';

const PAGE_SIZE = 10;

const RECURRING_STREAM_FRAGMENT = gql`
  fragment RecurringStream on RecurringTransaction {
    id
    description
    type
    frequency
    nextOccurrence
    isActive
    amountRange
    rawJson
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

// Minimal transaction fragment for recurring stream table display
const RecurringStreamTransactionFragment = gql`
  fragment RecurringStreamTransaction on Transaction {
    id
    amount
    date
    description
    isPending
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
      bank {
        id
        name
      }
    }
  }
  ${Entity.Avatar.Fragment}
`;

const GET_RECURRING_STREAM_TRANSACTIONS = gql`
  query GetRecurringStreamTransactions(
    $pagination: PaginationInput!
    $filters: TransactionFilters!
    $sortKey: TransactionSortKeys!
  ) {
    getTransactionsForUser(pagination: $pagination, filters: $filters, sortKey: $sortKey) {
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        node {
          ...RecurringStreamTransaction
        }
      }
    }
  }
  ${RecurringStreamTransactionFragment}
`;

type Transaction = NonNullable<
  NonNullable<GetRecurringStreamTransactionsQuery['getTransactionsForUser']>['edges']
>[number]['node'];

export type RecurringStream = RecurringStreamFragment;

interface IBaseRecurringStreamTableProps {
  recurringStream: RecurringStream;
  transactions: Transaction[];
  dateRange?: CalendarDateRange;
  isLoadingInitial?: boolean;
  isLoadingMore?: boolean;
  hasError?: boolean;
  hasNextPage: boolean;
  onLoadMore: () => void;
  emptyStateProps?: IEmptyTableStateProps;
}

export const BaseRecurringStreamTable = ({
  recurringStream,
  transactions,
  dateRange,
  isLoadingInitial = false,
  isLoadingMore = false,
  hasError = false,
  hasNextPage,
  onLoadMore,
  emptyStateProps,
}: IBaseRecurringStreamTableProps) => {
  const toggledStreams = useToggledStreamsParam();
  const isCollapsed = !recurringStream.isActive !== toggledStreams.isToggled(recurringStream.id);
  const navigate = useNavigateWithDateRange();
  const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const isWideCard = useContainerBreakpoint(containerRef, 'md');
  const showYearlySpend = useContainerBreakpoint(containerRef, 'sm');

  const title = recurringStream.description;
  const yearlySpend = calculateYearlySpend(recurringStream.amountRange, recurringStream.frequency);
  const dateRangeTextRaw = useDateRangeDisplayText(dateRange);
  const dateRangeText = dateRange ? dateRangeTextRaw : undefined;

  let nextContent: React.ReactNode;
  if (!recurringStream.isActive) {
    nextContent = <span className="text-tertiary text-sm font-medium italic">Inactive</span>;
  } else if (recurringStream.nextOccurrence) {
    nextContent = (
      <Tooltip title={formatCalendarDate(recurringStream.nextOccurrence, 'medium')}>
        <TooltipTrigger>
          <span className="text-primary text-sm font-medium">{formatDaysUntil(recurringStream.nextOccurrence)}</span>
        </TooltipTrigger>
      </Tooltip>
    );
  } else {
    nextContent = <span className="text-primary text-sm font-medium">Unknown</span>;
  }

  const trailingContent = (
    <div className="flex shrink-0 items-center gap-3">
      <span className={cx('flex whitespace-nowrap', isWideCard ? 'items-center gap-2' : 'flex-col items-end gap-0.5')}>
        <span className="text-tertiary text-sm font-medium">{formatRecurringType(recurringStream.type)}</span>
        {isWideCard && <span className="text-tertiary text-sm font-medium">•</span>}
        <span className="flex items-center gap-1">
          <span className="text-tertiary text-sm font-medium">{getFrequencyLabel(recurringStream.frequency)}</span>
          <span className="text-primary text-sm font-medium">{getAverageAmountLabel(recurringStream.amountRange)}</span>
          {showYearlySpend && <span className="text-tertiary text-sm font-medium">({toCurrency(yearlySpend)}/yr)</span>}
        </span>
        {isWideCard && <span className="text-tertiary text-sm font-medium">•</span>}
        <span className="flex items-center gap-1">
          <span className="text-tertiary text-sm font-medium">Next:</span>
          {nextContent}
        </span>
      </span>
      <CollapseButton isCollapsed={isCollapsed} onToggle={() => toggledStreams.toggle(recurringStream.id)} />
    </div>
  );

  return (
    <TableCard.Root ref={containerRef} className="w-full min-w-0">
      <TableCard.Header
        className="group/stream-header"
        trailingInline
        title={title}
        subtitle={dateRangeText}
        badge={
          <EditButton
            aria-label="Edit name"
            onClick={() => setIsRenameModalOpen(true)}
            className="shrink-0 opacity-0 transition-opacity focus-visible:opacity-100 group-hover/stream-header:opacity-100"
          />
        }
        contentTrailing={trailingContent}
      />
      {!isCollapsed && (
        <>
          {isLoadingInitial && <LoadingTableState />}
          {!isLoadingInitial && hasError && <ErrorTableState />}
          {!isLoadingInitial && !hasError && transactions.length === 0 && emptyStateProps && (
            <EmptyTableState {...emptyStateProps} />
          )}
          {!isLoadingInitial && !hasError && transactions.length > 0 && (
            <Table aria-label="Recurring stream transactions table" className="w-full">
              <Table.Header>
                <Table.Head label="Date" className="px-5" isRowHeader />
                <Table.Head label="" className="px-2" />
                <Table.Head label="Amount" className="flex justify-end pl-4" />
              </Table.Header>
              <Table.Body items={transactions}>
                {(transaction) => (
                  <Table.Row
                    key={transaction.id}
                    className="h-14 cursor-pointer"
                    onClick={() => {
                      void navigate(PATHS.TRANSACTION(transaction.id));
                    }}
                  >
                    <Table.Cell className="w-0 p-4">
                      <div className="text-primary text-base font-medium">{formatCalendarDate(transaction.date)}</div>
                    </Table.Cell>
                    <Table.Cell className="w-full max-w-0 px-2 py-5">
                      <div className="max-w-full truncate">
                        {transaction.financialAccount ? (
                          <AccountSecondaryElement financialAccount={transaction.financialAccount} showName={true} />
                        ) : null}
                      </div>
                    </Table.Cell>
                    <Table.Cell className={cx('w-0 pl-4', transaction.isPending && 'pt-2.75 pb-2.75')}>
                      <div className="flex flex-col items-end">
                        <div
                          className={cx(
                            'text-base font-medium',
                            Number(transaction.amount) < 0 ? 'text-success-primary' : 'text-primary'
                          )}
                        >
                          {toCurrency(Math.abs(Number(transaction.amount)), {
                            showSign: Number(transaction.amount) < 0,
                          })}
                        </div>
                        {transaction.isPending && <div className="text-tertiary text-xs italic">Pending</div>}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
          {!isLoadingInitial && !hasError && hasNextPage && (
            <div className="bg-secondary_subtle border-secondary flex items-center justify-center gap-3 border-t px-6 pb-4 pt-3">
              <Button size="md" color="secondary" onClick={onLoadMore} isLoading={isLoadingMore}>
                See more transactions
              </Button>
            </div>
          )}
        </>
      )}
      <UpdateRecurringTransactionModal
        recurringTransaction={recurringStream}
        isOpen={isRenameModalOpen}
        onOpenChange={setIsRenameModalOpen}
      />
    </TableCard.Root>
  );
};

interface IRecurringStreamTableProps {
  recurringStream: RecurringStream;
  dateRange?: CalendarDateRange;
  onDateRangeChange?: (range: CalendarDateRange) => void;
}

const RecurringStreamTable = ({ recurringStream, dateRange, onDateRangeChange }: IRecurringStreamTableProps) => {
  const filters = React.useMemo<TransactionFilters>(() => {
    const filter: TransactionFilters = {
      recurringTransactionId: recurringStream.id,
    };

    if (dateRange) {
      filter.startDate = dateRange.start.toString() as unknown as CalendarDate;
      filter.endDate = dateRange.end.toString() as unknown as CalendarDate;
    }

    return filter;
  }, [recurringStream.id, dateRange]);

  const { data, loading, error, fetchMore } = useQuery<
    GetRecurringStreamTransactionsQuery,
    GetRecurringStreamTransactionsQueryVariables
  >(GET_RECURRING_STREAM_TRANSACTIONS, {
    variables: {
      pagination: {
        cursor: undefined,
        limit: PAGE_SIZE,
        direction: PaginationDirection.Forward,
      },
      filters,
      sortKey: TransactionSortKeys.Date,
    },
    notifyOnNetworkStatusChange: true,
  });

  const handleLoadMore = React.useCallback(async () => {
    const pageInfo = data?.getTransactionsForUser?.pageInfo;
    if (pageInfo?.endCursor) {
      await fetchMore({
        variables: {
          pagination: {
            cursor: pageInfo.endCursor,
            limit: PAGE_SIZE,
            direction: PaginationDirection.Forward,
          },
          filters,
        },
      });
    }
  }, [data, fetchMore, filters]);

  const transactions = data?.getTransactionsForUser?.edges?.map((edge) => edge.node) ?? [];
  const hasNextPage = data?.getTransactionsForUser?.pageInfo?.hasNextPage ?? false;
  const isLoadingMore = loading && transactions.length > 0;

  const handleDateRangeChange = React.useCallback(
    (range: CalendarDateRange) => {
      onDateRangeChange?.(range);
    },
    [onDateRangeChange]
  );

  const emptyStateProps = useHasTransactionsForEmptyState({
    filters,
    isLoading: loading,
    hasRows: transactions.length > 0,
    currentDateRange: dateRange,
    onViewTransactions: handleDateRangeChange,
  });

  return (
    <BaseRecurringStreamTable
      recurringStream={recurringStream}
      transactions={transactions}
      dateRange={dateRange}
      isLoadingInitial={loading && transactions.length === 0}
      isLoadingMore={isLoadingMore}
      hasError={Boolean(error)}
      hasNextPage={hasNextPage}
      onLoadMore={handleLoadMore}
      emptyStateProps={emptyStateProps}
    />
  );
};

RecurringStreamTable.Fragment = RECURRING_STREAM_FRAGMENT;

export default RecurringStreamTable;
